'use client'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useRouter } from 'next/navigation'

import { getAtImageVariant } from '../../atoms/at-image/at-image.props'
import { Media, OrMedia } from '../../organisms'
import { AtChip, AtPillDropdown, DropdownOption, MapPin } from '../../atoms'
import { getCategoryGroup } from '../../organisms/or-filter-section/category-groups'
import { useGeoCity } from '../../utils/geo-city'
import { findMatchingOption } from '../../utils/match-option'
import { cn } from '../../utils/cn'
import { useCtaAnalytics } from '../../utils/cta-analytics'

type SolutionKey = 'workspaces' | 'meetings-and-day-access' | 'events'

interface SolutionConfig {
  key: SolutionKey
  label: string
  description: string
  buttonLabel: string
  categoryKey: string
}

/** CMS row shape (Sanity `solutions` array items). */
export interface MlBannerHomeSearchSolution {
  solutionKey: string
  label: string
  description: string
  buttonLabel: string
}

const DEFAULT_SOLUTION_BY_KEY: Record<SolutionKey, SolutionConfig> = {
  'meetings-and-day-access': {
    key: 'meetings-and-day-access',
    label: 'Meetings & Day Passes',
    description: 'Spaces for the day or the hour. Choose where and how you work.',
    buttonLabel: 'Book a Space',
    categoryKey: 'meeting-rooms-and-day-passes',
  },
  workspaces: {
    key: 'workspaces',
    label: 'Offices & Suites',
    description: 'Coworking and private suites that grow and evolve with your team.',
    buttonLabel: 'Find an office',
    categoryKey: 'workspaces',
  },
  events: {
    key: 'events',
    label: 'Event Venues',
    description: 'Fully-serviced venues for gatherings of every size, in standout settings.',
    buttonLabel: 'Plan an Event',
    categoryKey: 'events',
  },
}

const DEFAULT_SOLUTION_ORDER: SolutionKey[] = ['meetings-and-day-access', 'workspaces', 'events']

const SOLUTION_KEYS = new Set<string>(DEFAULT_SOLUTION_ORDER)

function isSolutionKey(value: string): value is SolutionKey {
  return SOLUTION_KEYS.has(value)
}

function mergeHomeSearchSolutions(cmsSolutions?: MlBannerHomeSearchSolution[]): {
  byKey: Record<SolutionKey, SolutionConfig>
  order: SolutionKey[]
} {
  const base: Record<SolutionKey, SolutionConfig> = { ...DEFAULT_SOLUTION_BY_KEY }

  if (!cmsSolutions?.length) {
    return { byKey: base, order: [...DEFAULT_SOLUTION_ORDER] }
  }

  const merged: Record<SolutionKey, SolutionConfig> = { ...base }
  const cmsOrder: SolutionKey[] = []
  const seen = new Set<SolutionKey>()

  for (const row of cmsSolutions) {
    if (!isSolutionKey(row.solutionKey) || seen.has(row.solutionKey)) continue
    seen.add(row.solutionKey)
    cmsOrder.push(row.solutionKey)
    const def = base[row.solutionKey]
    merged[row.solutionKey] = {
      ...def,
      label: row.label?.trim() || def.label,
      description: row.description?.trim() || def.description,
      buttonLabel: row.buttonLabel?.trim() || def.buttonLabel,
    }
  }

  const rest = DEFAULT_SOLUTION_ORDER.filter((k) => !seen.has(k))
  const order = cmsOrder.length > 0 ? [...cmsOrder, ...rest] : [...DEFAULT_SOLUTION_ORDER]

  return { byKey: merged, order }
}

function resolveInitialSolutionKey(order: SolutionKey[], defaultSolutionKey?: string | null): SolutionKey {
  if (defaultSolutionKey && isSolutionKey(defaultSolutionKey) && order.includes(defaultSolutionKey)) {
    return defaultSolutionKey
  }
  return order[0]
}

/** Option shown when no location filter is selected. Empty value = no location param in URL. */
const ALL_LOCATION_OPTION: DropdownOption = {
  value: '',
  label: 'All Location',
  id: 'all-location',
}

/** Option to reset selection to geo-matched current location. Only shown when selection is not the geo match. */
const CURRENT_LOCATION_OPTION: DropdownOption = {
  value: '__current__',
  label: 'Current Location',
  id: 'current-location',
  iconLeft: <MapPin className="w-4 h-5" />,
}

export interface MlBannerHomeSearchProps {
  name?: string
  headline?: string
  description?: string
  media?: Media
  mediaDesktop?: Media
  locationOptions?: DropdownOption[]
  /** City from geo (e.g. Netlify Edge). Pre-selects location if it matches an option. */
  defaultGeoCity?: string | null
  /** Sanity: optional overrides per product tab; order sets tab order. */
  solutions?: MlBannerHomeSearchSolution[]
  /** Sanity: which tab is selected on load (must match a known solution key). */
  defaultSolutionKey?: string | null
}

export const MlBannerHomeSearch = ({
  media,
  mediaDesktop,
  headline,
  description,
  locationOptions: propLocationOptions,
  defaultGeoCity: defaultGeoCityProp,
  solutions: cmsSolutions,
  defaultSolutionKey,
}: MlBannerHomeSearchProps) => {
  const geoCityFromContext = useGeoCity()
  const defaultGeoCity = defaultGeoCityProp ?? geoCityFromContext

  const router = useRouter()
  const { logClick } = useCtaAnalytics()

  const { byKey: solutionByKey, order: solutionOrder } = useMemo(
    () => mergeHomeSearchSolutions(cmsSolutions),
    [cmsSolutions]
  )

  const [selectedSolution, setSelectedSolution] = useState<SolutionKey>(() => {
    const { order } = mergeHomeSearchSolutions(cmsSolutions)
    return resolveInitialSolutionKey(order, defaultSolutionKey)
  })
  const [selectedLocation, setSelectedLocation] = useState<DropdownOption>(() => {
    if (defaultGeoCity && propLocationOptions?.length) {
      const match = findMatchingOption(defaultGeoCity, propLocationOptions)
      if (match) return match
    }
    return ALL_LOCATION_OPTION
  })
  const [selectedViaGeolocation, setSelectedViaGeolocation] = useState(() => {
    if (defaultGeoCity && propLocationOptions?.length) {
      const match = findMatchingOption(defaultGeoCity, propLocationOptions)
      return !!match
    }
    return false
  })
  const [locationOptions, setLocationOptions] = useState<DropdownOption[]>(() => [
    ALL_LOCATION_OPTION,
    ...(propLocationOptions || []),
  ])

  const hasAppliedInitialGeoMatch = useRef(false)

  const cityOptions = useMemo(
    () => locationOptions.filter((o) => o.value !== '' && o.id !== 'current-location'),
    [locationOptions]
  )
  const geoMatch = useMemo(
    () => (defaultGeoCity && cityOptions.length ? findMatchingOption(defaultGeoCity, cityOptions) : undefined),
    [defaultGeoCity, cityOptions]
  )

  const displayOptions = useMemo(() => {
    const isGeoMatchSelected = geoMatch && selectedLocation?.value === geoMatch.value
    if (geoMatch && !isGeoMatchSelected) {
      return [ALL_LOCATION_OPTION, CURRENT_LOCATION_OPTION, ...cityOptions]
    }
    return locationOptions
  }, [locationOptions, cityOptions, geoMatch, selectedLocation?.value])

  const handleFindSpace = useCallback(() => {
    const categoryKey = solutionByKey[selectedSolution].categoryKey
    const solutionGroup = getCategoryGroup(categoryKey)
    if (!solutionGroup) return

    const typesParam = solutionGroup.types.join(',')
    const urlParams = new URLSearchParams()
    if (typesParam) urlParams.set('type', typesParam)
    const locationValue =
      selectedLocation?.value === '__current__' && geoMatch ? geoMatch.value : selectedLocation?.value
    if (locationValue) urlParams.set('location', locationValue)

    const queryString = urlParams.toString()
    const href = `/spaces${queryString ? `?${queryString}` : ''}`

    logClick({
      text: solutionByKey[selectedSolution].buttonLabel,
      style: 'button',
      href,
      component: 'MlBannerHomeSearch',
    })

    router.push(href)
  }, [selectedSolution, selectedLocation, geoMatch, router, solutionByKey, logClick])

  const handleLocationChange = useCallback(
    (option: DropdownOption | undefined) => {
      if (!option) {
        setSelectedLocation(ALL_LOCATION_OPTION)
        setSelectedViaGeolocation(false)
        return
      }
      if (option.value === '__current__' && geoMatch) {
        setSelectedLocation(geoMatch)
        setSelectedViaGeolocation(true)
        return
      }
      setSelectedLocation(option)
      setSelectedViaGeolocation(false)
    },
    [geoMatch]
  )

  // Use prop locationOptions if provided, otherwise load defaults. Always prepend All Locations.
  useEffect(() => {
    if (propLocationOptions && propLocationOptions.length > 0) {
      setLocationOptions([ALL_LOCATION_OPTION, ...propLocationOptions])
    } else if (locationOptions.length <= 1) {
      // Fallback default locations if not provided
      const defaultLocations: DropdownOption[] = [
        { value: 'new-york', label: 'New York', id: 'new-york' },
        { value: 'san-francisco', label: 'San Francisco', id: 'san-francisco' },
        { value: 'los-angeles', label: 'Los Angeles', id: 'los-angeles' },
      ]
      setLocationOptions([ALL_LOCATION_OPTION, ...defaultLocations])
    }
  }, [propLocationOptions])

  // Apply geo match only once on initial load. Never override user's selection.
  useEffect(() => {
    if (hasAppliedInitialGeoMatch.current || !defaultGeoCity) return
    const opts = propLocationOptions?.length ? propLocationOptions : locationOptions.filter((o) => o.value !== '')
    if (!opts.length) return
    hasAppliedInitialGeoMatch.current = true
    const match = findMatchingOption(defaultGeoCity, opts)
    if (match) {
      setSelectedLocation(match)
      setSelectedViaGeolocation(true)
    }
  }, [defaultGeoCity, propLocationOptions, locationOptions])

  return (
    <div className="flex flex-col w-full">
      {/* Banner: media + overlay + headline. From lg, search floats on the right */}
      <div className="relative flex flex-col items-center justify-center min-h-[44rem] md:min-h-[32rem] lg:min-h-[42.5rem]">
        {media && <OrMedia {...media} className={cn(getAtImageVariant('background'), 'md:hidden')} />}
        {(mediaDesktop ?? media) && (
          <OrMedia {...(mediaDesktop ?? media)!} className={cn(getAtImageVariant('background'), 'hidden md:block')} />
        )}
        <div className="absolute inset-0 bg-neutrals-black/[.56]" aria-hidden />

        <div className="flex flex-1 slg:px-12 lg:px-41.5 relative z-10 w-full py-0 md:py-17 slg:py-17">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 max-w-screen-lg md:mx-auto">
            {/* Headline and Description */}
            {(headline || description) && (
              <div className="flex-1 content-end px-4 sm:px-8 slg:px-0 mb-8 lg:mb-0 lg:flex-1 text-neutrals-white text-shadow shadow-light-on-dark text-center lg:text-left">
                {headline && (
                  <h2 className="font-heading font-medium text-32 leading-9 slg:text-40 slg:leading-12 lg:text-56 lg:leading-16 mb-4">
                    {headline}
                  </h2>
                )}
                {description && (
                  <p className="leading-6 slg:text-20 slg:leading-7.5 lg:text-24 lg:leading-9">{description}</p>
                )}
              </div>
            )}

            <div className="bg-white/20 backdrop-blur-md border-[rgba(255,255,255,0.6)] border-[0.5px] rounded-0 sm:rounded-8 md:rounded-[1.5rem] p-6 w-full md:max-w-[569px] mt-auto md:mt-0 mx-auto lg:mx-0 lg:flex-shrink-0 shadow-[8px_8px_40px_0px_#0000001A]">
              <div className="flex gap-2 mb-3">
                {solutionOrder.map((key) => {
                  const solution = solutionByKey[key]
                  const isSelected = selectedSolution === solution.key
                  const chipClassName = cn(
                    '!h-auto !text-16 !min-w-0 !flex-1 !rounded-[24px] p-3 md:!px-5 transition-colors duration-200 ease-out',
                    isSelected
                      ? '!border-neutrals-white !bg-neutrals-white !text-primary-500'
                      : '!border-neutrals-white/60 !bg-neutrals-white/15 !text-neutrals-white'
                  )
                  return (
                    <AtChip
                      key={solution.key}
                      label={solution.label}
                      selected={isSelected}
                      onClick={() => setSelectedSolution(solution.key)}
                      className={chipClassName}
                    />
                  )
                })}
              </div>
              <div className="flex items-center justify-center gap-2 mt-4 mb-6">
                <p className="text-14 text-center leading-1.5 text-neutrals-white min-h-[1.5rem] items-center animate-fade-in-up">
                  {solutionByKey[selectedSolution].description}
                </p>
              </div>
              <div className="mb-6">
                <AtPillDropdown
                  id="ml-banner-home-search-location"
                  label="All Location"
                  options={displayOptions}
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  valueIcon={
                    selectedViaGeolocation && geoMatch && selectedLocation?.value === geoMatch.value ? (
                      <MapPin className="w-4 h-5" />
                    ) : undefined
                  }
                  className="w-full"
                  pillClassName="!rounded-8 !text-16 !p-4 !h-auto !text-neutrals-white !border-neutrals-white"
                  pillArrowClassName="!text-neutrals-white"
                  dropdownClassName="!w-[496px]"
                />
              </div>
              <button
                onClick={handleFindSpace}
                className="w-full bg-primary-500 text-neutrals-white font-sans tracking-[0.16em] text-16 leading-6 py-4 px-6 rounded-lg hover:bg-primary-600 transition-colors uppercase"
              >
                {solutionByKey[selectedSolution].buttonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
